import { pca } from '../auth/msalConfig';
import { mailRequest } from '../auth/msalConfig';

/**
 * Sends a notification email from the signed-in user via Microsoft Graph's
 * /me/sendMail endpoint. Used after a team member submits a request on the
 * My Account page. Goes To the admin list and CCs the submitter and
 * their managers (see REQUEST_NOTIFICATION_TO / requestRecipients below).
 *
 * Requires the `Mail.Send` delegated scope on the Proxemics Portal app in
 * Entra — we request it separately from the default Sites.ReadWrite.All
 * so most sign-ins don't need to consent to email-sending. The first time
 * a user actually submits a request, MSAL may prompt them to consent.
 *
 * Non-fatal: the caller treats email failure as a warning, not an error.
 * The underlying SharePoint row was already created successfully, so we
 * don't want an email outage to block the UI from reporting success.
 */

/** Acquire a Graph token with the Mail.Send scope. Popup-fallback so a
 *  missing-consent case still completes instead of silently failing. */
async function getMailToken() {
  const account = pca.getActiveAccount() || pca.getAllAccounts()[0];
  if (!account) {
    throw new Error('No signed-in account; cannot send mail.');
  }
  try {
    const result = await pca.acquireTokenSilent({ ...mailRequest, account });
    return result.accessToken;
  } catch {
    const result = await pca.acquireTokenPopup(mailRequest);
    return result.accessToken;
  }
}

/**
 * Send an email as the currently signed-in user. Pass either `body` (plain
 * text) or `html` (HTML). If both are passed, `html` wins. Most callers in
 * the portal use the HTML path so we can bold labels and lay things out
 * with proper spacing; the plain-text path is kept for simple notifications.
 *
 * Inline images: pass `attachments` as an array of Graph fileAttachment
 * objects. To reference one in the HTML, set `isInline: true` and
 * `contentId: "foo"`, then embed `<img src="cid:foo">` in the body.
 *
 * @param {object} opts
 * @param {string[]} opts.to         — list of recipient email addresses
 * @param {string[]} [opts.cc]       — optional CC list
 * @param {string}   opts.subject
 * @param {string}   [opts.body]     — plain text body (\n line breaks)
 * @param {string}   [opts.html]     — HTML body; takes precedence over body
 * @param {object[]} [opts.attachments] — Graph fileAttachment array (inline OK)
 */
export async function sendMail({ to, cc, subject, body, html, attachments }) {
  const token = await getMailToken();
  const toRecipients = (to || [])
    .filter(Boolean)
    .map((address) => ({ emailAddress: { address } }));
  const ccRecipients = (cc || [])
    .filter(Boolean)
    .map((address) => ({ emailAddress: { address } }));
  if (toRecipients.length === 0) {
    throw new Error('sendMail: at least one "to" address is required.');
  }
  const messageBody = html
    ? { contentType: 'HTML', content: html }
    : { contentType: 'Text', content: body || '' };
  const message = {
    subject,
    body: messageBody,
    toRecipients,
    ccRecipients,
  };
  if (Array.isArray(attachments) && attachments.length > 0) {
    message.attachments = attachments;
  }
  const payload = {
    message,
    // saveToSentItems = true keeps a copy in the submitter's Sent Items,
    // so there's an auditable trail from the user's own mailbox as well as
    // the SharePoint row. Useful for "did this actually go through?"
    saveToSentItems: true,
  };
  const res = await fetch('https://graph.microsoft.com/v1.0/me/sendMail', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    // Graph returns a JSON error body with a useful message; try to parse
    // it so the console message points us straight at the fix (usually
    // "missing Mail.Send permission — grant consent in Entra").
    let detail = '';
    try {
      const err = await res.json();
      detail = err?.error?.message || JSON.stringify(err);
    } catch {
      detail = await res.text().catch(() => '');
    }
    throw new Error(`sendMail failed (${res.status}): ${detail}`);
  }
}

// =====================================================================
// Request-specific helpers
// =====================================================================

/** Human label per request type so the email subject line reads naturally. */
const TYPE_LABELS = {
  Vest: 'Vest',
  Shirt: 'Shirt',
  'Business Cards': 'Business Cards',
  'Badge Card': 'Badge Card',
  'Address Change': 'Address Change',
  'Phone Change': 'Phone Change',
  'Emergency Contact Change': 'Emergency Contact Change',
  'Shirt Size Change': 'Shirt Size Change',
  'Vest Size Change': 'Vest Size Change',
};

/** Lowercase noun phrase used in the greeting sentence ("your request for
 *  ___"). Kept explicit per type so articles (a / an / none) read naturally. */
const REQUEST_ARTICLE = {
  Vest: 'a vest',
  Shirt: 'a shirt',
  'Business Cards': 'business cards',
  'Badge Card': 'a badge card',
  'Address Change': 'an address change',
  'Phone Change': 'a phone change',
  'Emergency Contact Change': 'an emergency contact update',
  'Shirt Size Change': 'a shirt size update',
  'Vest Size Change': 'a vest size update',
};

/** Shipping-request types ship something physical to the submitter, so
 *  the notification email should include a "Ship to:" block resolved
 *  from the submitter's team record. Address/Phone changes don't ship
 *  anything, so they're excluded. */
const SHIPPING_TYPES = new Set(['Vest', 'Shirt', 'Business Cards', 'Badge Card']);

/** Escape values that will be inlined into HTML. Graph's sendMail expects
 *  clean HTML, and request fields like Notes or a custom business-card
 *  title line can include characters that would break rendering otherwise. */
function escapeHtml(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Production URL for the portal — used as the clickable link in the email
 *  footer. Display text stays the same in all environments so the email
 *  reads consistently regardless of where it was sent from. */
const PORTAL_URL = 'https://proxemicsconsulting.com/portal';
const PORTAL_URL_DISPLAY = 'proxemicsconsulting.com/portal';

/** Brand orange, used for the footer link to match the rest of the portal. */
const BRAND_ORANGE = '#E87722';

/** contentId used to reference the inline logo from the HTML body. */
const LOGO_CID = 'proxportal-logo';

/**
 * Build an HTML body describing a submitted request. Shows only the fields
 * the form actually captured for this type. Field labels are bold so the
 * approver can scan the details at a glance; the "Ship to:" block is bold
 * for the same reason. For auto-applied changes (Address / Phone) we swap
 * the "for your manager's approval" line for an "already applied" note so
 * the submitter knows no action is needed.
 *
 * @param {boolean} includeLogo — true if the caller successfully attached
 *   the inline logo; when false the logo <img> is omitted so the email
 *   doesn't render a broken-image placeholder.
 */
function buildHtmlBody({
  request,
  submitter,
  submitterName,
  submitterEmail,
  appliedAlready,
  includeLogo,
}) {
  const firstName = (submitterName || '').trim().split(/\s+/)[0] || 'there';
  const article =
    REQUEST_ARTICLE[request.RequestType] ||
    (TYPE_LABELS[request.RequestType] || 'this request').toLowerCase();

  const parts = [];
  parts.push(`<p>Hi ${escapeHtml(firstName)},</p>`);
  parts.push(
    `<p>We received your request for ${escapeHtml(article)} on the Proxemics Portal.</p>`,
  );
  if (appliedAlready) {
    parts.push(
      `<p>This change has already been applied automatically to your Proxemics Team ` +
        `record — no further action is needed. Sending details below for your records.</p>`,
    );
  } else {
    parts.push(
      `<p>Sending details below for your manager's approval.</p>`,
    );
  }

  // Type-specific detail block. Each line is "<b>Label:</b> value" so the
  // labels stand out and the values read as one continuous list.
  const detailLines = [];
  const push = (label, val) => {
    if (val !== undefined && val !== null && String(val).trim() !== '') {
      detailLines.push(
        `<div><strong>${escapeHtml(label)}:</strong> ${escapeHtml(val)}</div>`,
      );
    }
  };
  switch (request.RequestType) {
    case 'Vest':
    case 'Shirt':
      push('Size', request.Size);
      push('Quantity', request.Quantity);
      push('Fit', request.Fit);
      push('Style', request.ShirtStyle);
      push('Color', request.VestColor);
      break;
    case 'Business Cards':
      push('Name on card', request.CardNameLine);
      push('Title on card', request.CardTitleLine);
      push('Phone on card', request.CardPhoneLine);
      push('Email on card', request.CardEmailLine);
      push('Quantity', request.CardQuantity);
      break;
    case 'Badge Card':
      push('First name', request.BadgeFirstName);
      push('Last name', request.BadgeLastName);
      push('Job title', request.BadgeJobTitle);
      push('Email', request.CardEmailLine);
      push('Phone', request.CardPhoneLine);
      break;
    case 'Address Change':
      push('Street', request.NewStreet);
      push('City', request.NewCity);
      push('State / Province', request.NewState);
      push('Zip / Postal Code', request.NewZip);
      push('Country', request.NewCountry);
      break;
    case 'Phone Change':
      push('New phone', request.NewPhone);
      break;
    case 'Emergency Contact Change':
      push('Name', request.NewEmergencyContactName);
      push('Phone', request.NewEmergencyContactPhone);
      push('Relation', request.NewEmergencyContactRelation);
      break;
    case 'Shirt Size Change':
      push('New shirt size', request.NewShirtSize);
      push('New shirt fit', request.NewShirtFit);
      break;
    case 'Vest Size Change':
      push('New vest size', request.NewVestSize);
      // When the color is "Other", the fill-in captures the actual color
      // — prefer that over the literal word "Other" for readability.
      push(
        'New vest color',
        request.NewVestColor === 'Other'
          ? request.NewVestColorOther || 'Other'
          : request.NewVestColor,
      );
      break;
    default:
      break;
  }
  push('Notes', request.Notes);
  if (detailLines.length > 0) {
    parts.push(`<div>${detailLines.join('')}</div>`);
  }

  // Ship-to block — only for physical goods, where we need to know where
  // to mail the item. Pulled from the submitter's team record (the form
  // required them to confirm that address before submitting, so this is
  // the address they agreed to ship to).
  if (SHIPPING_TYPES.has(request.RequestType) && submitter) {
    const addrLines = [
      submitter.MailingStreet,
      [submitter.MailingCity, submitter.MailingState, submitter.MailingZip]
        .filter((s) => s && String(s).trim() !== '')
        .join(', '),
      submitter.MailingCountry,
    ].filter((s) => s && String(s).trim() !== '');
    if (addrLines.length > 0) {
      const addrHtml = addrLines
        .map((line) => `<div>${escapeHtml(line)}</div>`)
        .join('');
      parts.push(
        `<p style="margin-bottom:4px;"><strong>Ship to:</strong></p>` +
          `<div style="margin-left:16px;">${addrHtml}</div>`,
      );
    }
  }

  // Footer: who submitted (just the name — email is omitted because the
  // approver can reply directly to this message to reach them). Sign-off +
  // branded logo + portal link round out the email so every message feels
  // like it's coming from the portal itself, not a bare automation.
  parts.push(
    `<p><strong>Submitted by:</strong> ${escapeHtml(submitterName || '(unknown)')}</p>`,
  );
  parts.push(`<p>Thank you,<br>Proxemics Portal</p>`);

  if (includeLogo) {
    parts.push(
      `<p style="margin-top:24px;margin-bottom:8px;">` +
        `<img src="cid:${LOGO_CID}" alt="ProxPortal" style="max-width:220px;height:auto;border:0;display:block;">` +
        `</p>`,
    );
  }
  parts.push(
    `<p style="margin-top:8px;">` +
      `<a href="${PORTAL_URL}" style="color:${BRAND_ORANGE};text-decoration:underline;">` +
      `${escapeHtml(PORTAL_URL_DISPLAY)}</a>` +
      `</p>`,
  );

  // Wrap everything in a single container that sets the font so Outlook
  // doesn't fall back to Times New Roman on Windows.
  return (
    `<div style="font-family:Arial,Helvetica,sans-serif;color:#1f2937;font-size:14px;line-height:1.5;">` +
    parts.join('') +
    `</div>`
  );
}

/**
 * Fetch the ProxPortal logo from /public and return it as a base64 string
 * (no data: prefix) so it can be attached to an email inline. Returns null
 * if the fetch fails for any reason — the caller skips the <img> in that
 * case so we never ship a broken-image placeholder.
 */
async function fetchLogoBase64() {
  try {
    const base = import.meta.env?.BASE_URL || '/';
    const url = `${base.replace(/\/$/, '')}/ProxPortal-FullColor.png`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = typeof reader.result === 'string' ? reader.result : '';
        const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : '';
        resolve(base64 || null);
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Who hears about a new request (per Jillian, Oct 2026).
 *
 *   To:  the fixed admin list below — Jillian, Deanna Gheldof, Judy Gheldof.
 *   CC:  the submitter (so they have a confirmation copy), plus their
 *        Liaison Manager(s) and Regional Manager when the roster has an
 *        email for them.
 *
 * Note the mail is sent FROM the submitter's own mailbox (/me/sendMail),
 * so a copy also lands in their Sent Items. Anyone already in To is not
 * repeated in CC, and the submitter is never CC'd to themselves when they
 * are on the admin list (otherwise Jillian would get two copies of her own
 * updates).
 */
export const REQUEST_NOTIFICATION_TO = [
  'jillian.lamb@proxemicsconsulting.com',
  'deanna.gheldof@proxemicsconsulting.com',
  'judy.gheldof@proxemicsconsulting.com',
];

const normEmail = (e) => String(e || '').trim().toLowerCase();
const normName = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

/** Legal "First Last" (or Title) for a roster record — matches how
 *  RegionalManager / liaisonManagers store names. */
function legalName(member) {
  const composed = [member?.FirstName, member?.LastName]
    .map((v) => String(v || '').trim())
    .filter(Boolean)
    .join(' ');
  return composed || String(member?.Title || '').trim();
}

/** Email for a stored manager name, looked up on the roster. '' if unknown. */
function emailForName(team, storedName) {
  const needle = normName(storedName);
  if (!needle) return '';
  const hit = (team || []).find(
    (m) =>
      (m?.EmploymentStatus || 'Active') !== 'Exited' &&
      (normName(legalName(m)) === needle || normName(m?.Title) === needle),
  );
  return normEmail(hit?.Email);
}

/**
 * Build the To / CC lists for a request notification. Pure; exported for
 * tests.
 *
 * @returns {{ to: string[], cc: string[] }}
 */
export function requestRecipients({ submitter, submitterEmail, team }) {
  const to = REQUEST_NOTIFICATION_TO.map(normEmail);
  const seen = new Set(to);
  const cc = [];
  const add = (addr) => {
    const e = normEmail(addr);
    if (!e || !e.includes('@') || seen.has(e)) return;
    seen.add(e);
    cc.push(e);
  };
  add(submitter?.Email || submitterEmail);
  for (const lm of Array.isArray(submitter?.liaisonManagers) ? submitter.liaisonManagers : []) {
    add(emailForName(team, lm));
  }
  add(emailForName(team, submitter?.RegionalManager));
  return { to, cc };
}

/**
 * Notify the admin list of a newly-submitted request, CC'ing the
 * submitter and their managers (see requestRecipients()).
 *
 * @param {object} opts
 * @param {object} opts.request        — the full request payload as saved
 * @param {object} opts.submitter      — the submitter's team record (may be null)
 * @param {string} opts.submitterName
 * @param {string} opts.submitterEmail
 * @param {Array}  [opts.team]         — decoded team roster, used to find
 *                                       manager emails
 * @param {boolean} [opts.appliedAlready] — true for Address/Phone auto-apply
 */
export async function notifyRequestSubmitted({
  request,
  submitter,
  submitterName,
  submitterEmail,
  team,
  appliedAlready = false,
}) {
  const typeLabel = TYPE_LABELS[request.RequestType] || 'Request';
  const who = submitterName || submitterEmail || 'a team member';
  const subject = `Proxemics Request: ${typeLabel} for ${who}`;

  // Pull the logo first so we know whether to include the <img> in the
  // body. Fetching it is a local /public request, so it's fast, but we
  // still fall back cleanly if for any reason it fails.
  const logoBase64 = await fetchLogoBase64();
  const attachments = logoBase64
    ? [
        {
          '@odata.type': '#microsoft.graph.fileAttachment',
          name: 'ProxPortal-FullColor.png',
          contentType: 'image/png',
          contentBytes: logoBase64,
          contentId: LOGO_CID,
          isInline: true,
        },
      ]
    : [];

  const html = buildHtmlBody({
    request,
    submitter,
    submitterName,
    submitterEmail,
    appliedAlready,
    includeLogo: !!logoBase64,
  });
  const { to, cc } = requestRecipients({ submitter, submitterEmail, team });
  await sendMail({
    to,
    cc,
    subject,
    html,
    attachments,
  });
}
